import React, { useState, useEffect, Fragment } from "react";
import { useDispatch } from "react-redux";
import * as Entities from "entities";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
// import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import { usePageTitle } from "../../utils/pageTitle";
import NotFound from "../NotFound";
import { showErrorAlert, showSuccessAlert } from "../../store/actions/alerts";
import ComponentLoader from "../../components/loader/ComponentLoader";

import { SYS_GET_DOCUMENT_URL, SYS_EDIT_DOCUMENT_URL } from "../../config";
import makeApiRequest from "../../utils/axios/new-axios";

import { permissions, types, checkAcl } from "../../utils/acl";

import SubmitButton from "../../components/button/SubmitButton";
import CancelButton from "../../components/button/CancelButton";
import TextField from "../../components/formik/TextField";
import CKEditor4 from "../../components/formik/CKEditor4";

// import { fromUnixtime } from "../../utils/time";


const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Name Required!"),
    data: Yup.string().trim().required("Data Required!")
})

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: "16px",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        width: "100%",
    },
}));

export default function Documents(props) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const pageTitle = usePageTitle("System Documents");
    const EDIT_ACCESS = checkAcl({ p: permissions.SYSTEM_SETTINGS_MANAGEMENT, t: types.VIEW_AND_EDIT });

    const docType = props.match.params.docType;

    const [notFound, setNotFound] = useState(false);
    const [doc, setDoc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState(null);

    useEffect(() => {
        makeApiRequest({
            url: `${SYS_GET_DOCUMENT_URL.url}/${docType}`,
            method: SYS_GET_DOCUMENT_URL.method,
        }).then(response => {
            setDoc(response.data);
            pageTitle(response.data.type);
            setIsLoading(false);
        }).catch((error) => {
            setIsLoading(false);
            if (!!error.response && error.response.status === 404) {
                setNotFound(true);
            } else {
                dispatch(
                    showErrorAlert(
                        "Document Fetch Error : " +
                        (!!error.response
                            ? `${error.response.status}! - ${error.response.data.errorMessage}`
                            : error.message)
                    )
                );
            }
        })
        // eslint-disable-next-line
    }, []); // [] is required to make sure that it runs only once

    const handleEdit = () => {
        setInitialValues({
            name: doc.name,
            data: doc.data
        })
        setEditMode(true);
    }

    const handleCancel = () => {
        setInitialValues(null);
        setEditMode(false);
    }

    const handleSubmit = (values, { setSubmitting }) => {
        let confirm = prompt('Please Enter "confirm" to update.')
        if (!!confirm && confirm.trim() === "confirm") {
            makeApiRequest({
                url: `${SYS_EDIT_DOCUMENT_URL.url}/${docType}`,
                method: SYS_EDIT_DOCUMENT_URL.method,
                data: values
            }).then(() => {
                setDoc({
                    ...doc,
                    ...values
                });
                setSubmitting(false);
                setEditMode(false);
                dispatch(showSuccessAlert("Updated successfully"));
            }).catch((error) => {
                setSubmitting(false);
                dispatch(
                    showErrorAlert(
                        "Document Fetch Error : " +
                        (!!error.response
                            ? `${error.response.status}! - ${error.response.data.errorMessage}`
                            : error.message)
                    )
                );
            })
        } else {
            setSubmitting(false);
        }
    }

    return (
        <Fragment>
            {!notFound ? (
                <Container style={{ marginTop: "20px", padding: "10px" }}>
                    <ComponentLoader isLoading={isLoading} />
                    {!isLoading && doc &&
                        <Paper className={classes.paper}>
                            {!editMode &&
                                <Fragment>
                                    <h3>{doc.name}</h3>
                                    <div
                                        style={{
                                            backgroundColor: "white",
                                            color: "black",
                                            padding: "20px",
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: Entities.decodeHTML5(
                                                doc.data.replace("&NewLine;", "")
                                            ),
                                        }}
                                    ></div>
                                    <br />
                                    {/* TODO: AFTER UPDATE, user details also needed to update in the state to see the updated user detail */}
                                    {/* <Typography variant="caption"> {`Last updated by: ${doc.updatedBy.name} on ${fromUnixtime(doc.updatedOn)}`} </Typography> */}
                                    {EDIT_ACCESS &&
                                        <Box style={{ display: "flex", justifyContent: "center", margin: "16px" }}>
                                            <Button variant="contained" color="primary"
                                                onClick={handleEdit}>
                                                Edit Document
                                            </Button>
                                        </Box>}

                                </Fragment>}
                            {EDIT_ACCESS &&
                                <Fragment>
                                    {editMode &&
                                        <Formik
                                            initialValues={initialValues}
                                            validationSchema={validationSchema}
                                            onSubmit={handleSubmit}>
                                            {(props) => {
                                                const { touched, errors, isSubmitting } = props;
                                                return (
                                                    <Form className={classes.form}>
                                                        <Field
                                                            component={TextField}
                                                            name="name"
                                                            margin="dense"
                                                            label="Title *"
                                                            fullWidth
                                                            helperText={touched.name && errors.name && errors.name}
                                                            error={!!touched.name && !!errors.name}
                                                            disabled={isSubmitting}
                                                            autoFocus
                                                        />
                                                        <Field
                                                            component={CKEditor4}
                                                            name="data"
                                                            disabled={isSubmitting}
                                                            fullWidth
                                                            config={{ height: 500 }}
                                                        />
                                                        <ErrorMessage name="data" >
                                                            {msg => <div style={{ color: 'red' }}>{msg}</div>}
                                                        </ErrorMessage>
                                                        <Box style={{ display: "flex", justifyContent: "space-evenly", margin: "16px" }}>
                                                            <CancelButton
                                                                onClick={handleCancel}
                                                                disabled={isSubmitting}
                                                            >
                                                                Cancel
                                                            </CancelButton>
                                                            <SubmitButton
                                                                disabled={isSubmitting}
                                                                isSubmitting={isSubmitting}
                                                                autoFocus
                                                            >
                                                                Update
                                                            </SubmitButton>
                                                        </Box>
                                                    </Form>
                                                );
                                            }}
                                        </Formik>
                                    }

                                </Fragment>
                            }
                        </Paper>
                    }

                </Container>
            ) : (
                <NotFound />
            )}
        </Fragment>
    )
}
